<template>
  <div
    class="toolbox-table"
    :class="[{ 'is-disabled': disabled }]"
    @dragleave.prevent="onDragLeave"
    @dragover.prevent="onDragOver"
  >
    <div>
      <v-data-table :headers="headers" :items="items" hide-default-footer>
        <template #headers="slotProps">
          <template v-for="row in slotProps.headers" :key="row.key">
            <tr>
              <template v-for="column in row" :key="column.key">
                <th class="metric-table-header" :colspan="column.colspan" :rowspan="column.rowspan">
                  <div
                    class="metric-table-title"
                    :class="[
                      currentContextUuids.indexOf((column as any)?.uuid) > -1
                        ? 'active-drop-zone'
                        : 'drop-zone',
                      (column as any)?.uuid ? 'tool-target' : undefined,
                    ]"
                    @dragover.prevent="(event) => onDragOver(event, (column as any)?.uuid)"
                    @drop.prevent="(event) => onDropToolOperator(event, (column as any)?.uuid)"
                    @click="(event) => select(event, (column as any)?.uuid)"
                    @mouseover="mouseOver = (column as any)?.uuid"
                    @mouseleave="mouseOver = undefined"
                  >
                    <span
                      class="mr-2 metric-table-column-title"
                      :class="column.sortable ? 'cursor-pointer' : ''"
                      @click="() => (column.sortable ? slotProps.toggleSort(column) : {})"
                      >{{ column.title }}</span
                    >
                    <template v-if="slotProps.isSorted(column)">
                      <v-icon :icon="slotProps.getSortIcon(column)"></v-icon>
                    </template>

                    <div v-if="mouseOver === (column as any)?.uuid" class="header-actions">
                      <v-icon
                        v-if="hasDeleteTool((column as any)?.uuid)"
                        class="cursor-pointer"
                        @click="remove((column as any)?.uuid)"
                      >
                        mdi-close
                      </v-icon>
                      <v-icon
                        v-if="
                          currentQuery &&
                          slotProps.headers.length > 2 &&
                          row == slotProps.headers[0]
                        "
                        class="cursor-pointer"
                        @click="emit('openFilterMenu', (column as any)?.uuid)"
                      >
                        mdi-filter
                      </v-icon>
                    </div>
                  </div>
                </th>
              </template>
            </tr>
          </template>
        </template>
        <template #item="slotProps">
          <tr>
            <td
              v-for="header in slotProps.columns"
              :key="header.key || ''"
              class="toolbox-table-column"
            >
              {{
                !!header.value
                  ? typeof header.value === 'function'
                    ? (header.value as Function)(slotProps.item)
                    : header.value
                  : ''
              }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useProjectStore } from '@/stores/projectStore'
  import { storeToRefs } from 'pinia'
  import {
    CustomMetricPreview,
    Filter,
    Query,
    Row,
    Select,
    Tool,
  } from '@/models/custom-metric.model'
  import { EventBus } from '@/event-bus'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import {
    hasToolboxAssist,
    removeAllToolBoxAssistFieldsFromSelect,
    removeExtraToolboxAssistFields,
  } from '@/utils/custom-metric-filters'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'
  import { VDataTable } from 'vuetify/lib/components/index.mjs'

  // Workaround for https://github.com/vuetifyjs/vuetify/issues/16680
  type VDataTableProps = InstanceType<typeof VDataTable>['$props']

  interface Props {
    query?: Query
    disabled?: boolean //Default false
    currentContextUuids: string[]
    tools?: Tool[]
  }

  const props = withDefaults(defineProps<Props>(), {
    query: undefined,
    disabled: false,
    onOpenFilterMenu: undefined,
    tools: undefined,
  })

  const emit = defineEmits<{
    droppedTool: [query: Query, tool: Tool, location: string]
    itemSelected: [uuid: string]
    openFilterMenu: [uuid: string]
  }>()

  const customMetricService = injectTyped(ServiceKeys.customMetrics)
  const { currentProject } = storeToRefs(useProjectStore())
  const { currentSnapshot } = storeToRefs(useSnapshotStore())
  const previewData = ref<CustomMetricPreview>({ columns: [], rows: [] })
  let currentPreviewDataQuery: Query | undefined

  const currentQuery = computed(() => props.query) // ref<Query | undefined>(props.query)
  const headers = computed((): VDataTableProps['headers'] =>
    !props.query
      ? [{ title: 'No query' }]
      : [
          {
            title: showQueryOperatorName(props.query),
            align: 'center',
            ...{ uuid: props.query.from?.uuid },
            children:
              props.query.from?.type === 'toolbox.assist'
                ? undefined
                : props.query.from.parameters && props.query.from.parameters.length > 0
                  ? props.query.from.parameters.map((constraint, fromIndex) => ({
                      title: getNameFromConstraint(constraint),
                      key: `constraint_${fromIndex}`,
                      align: 'center',
                      sortable: false,
                      uuid: constraint.uuid,
                      children:
                        props.query?.select.filter((select) => select.field.index == fromIndex)
                          .length == 0
                          ? undefined // children must be undefined if no children exist. Layout is broken if not.
                          : props.query?.select
                              .filter((select) => select.field.index == fromIndex)
                              .map((select, selIndex) => ({
                                key: `${fromIndex}_${selIndex}`,
                                sortable: select.field.type !== 'toolbox.assist',
                                title: displayName(select),
                                uuid: select.uuid,
                                value: (row: any) => getSelectValue(select, row),
                              })),
                    }))
                  : [
                      props.query.filter
                        ? {
                            title: getNameFromConstraint(props.query.filter),
                            sortable: false,
                            ...{ uuid: (props.query.filter as any)?.right?.uuid },
                            children: props.query?.select.map((select, selIndex) => ({
                              key: `${selIndex}`,
                              sortable: select.field.type !== 'toolbox.assist',
                              title: displayName(select),
                              uuid: select.uuid,
                              value: (row: any) => getSelectValue(select, row),
                            })),
                          }
                        : {
                            title: 'No filter or constraint',
                            sortable: false,
                            children: props.query?.select.map((select, selIndex) => ({
                              key: `${selIndex}`,
                              sortable: select.field.type !== 'toolbox.assist',
                              title: displayName(select),
                              uuid: select.uuid,
                              value: (row: any) => getSelectValue(select, row),
                            })),
                          },
                    ],
          },
        ],
  )
  const items = computed(() => previewData.value.rows)
  const mouseOver = ref<string>()

  watch(
    [currentQuery, currentSnapshot],
    () => currentQuery.value && loadCustomMetricsPreview(currentQuery.value),
  )

  // TODO is this event still used?
  EventBus.on('reloadCustomMetricsPreviewData', loadCustomMetricsPreview)

  const isDragging = ref(false)

  function findDeleteTool(uuid?: string): Tool | undefined {
    return props.tools?.find((t) => t.kind === 'delete' && (!uuid || t.targets.indexOf(uuid) >= 0))
  }
  function hasDeleteTool(uuid?: string) {
    return uuid && findDeleteTool(uuid)
  }

  function remove(uuid: string) {
    if (uuid && props.query) {
      const tool = findDeleteTool(uuid)
      if (tool) {
        emit('droppedTool', props.query, tool, uuid)
      }
    }
  }

  function onDragOver(event: any, uuid?: string) {
    // This is necessary to allow the drop
    event.preventDefault()
    if (uuid && !isDragging.value) {
      isDragging.value = true
    }
  }

  function onDragLeave(event: any) {
    event.preventDefault()
    if (isDragging.value) {
      isDragging.value = false
    }
  }

  function onDropToolOperator(event: any, uuid?: string) {
    if (uuid) {
      const data = JSON.parse(event.dataTransfer.getData('application/json'))
      const tool = JSON.parse(data.item) as Tool
      if (currentQuery.value) {
        emit('droppedTool', currentQuery.value, tool, uuid)
      }
    } else {
      console.error('Drop not allowed at elements without UUID')
    }
  }

  function select(event: any, uuid?: string) {
    if (uuid) {
      emit('itemSelected', uuid)
    } else {
      console.error('Item without UUID can not be selected')
    }
  }

  function displayName(item: string | Select): string {
    if (typeof item === 'string') {
      return item === '*' ? 'All Fields' : item
    }
    if (item.as) {
      return item.as
    }
    const field = item.field
    if (typeof field === 'object' && 'type' in field && field.type === 'toolbox.assist') {
      return '<New>'
    }
    if (typeof field === 'object' && 'attribute' in field && field.type === 'artifact.attribute') {
      return field.attribute
    }
    if (typeof field === 'object' && 'property' in field && field.type === 'artifact.property') {
      return field.property
    }
    return 'Undefined'
  }

  function getNameFromConstraint(constraint: Filter): string {
    if (constraint.type === 'eq' && constraint.right.type === 'literal')
      return constraint.right.value

    return '<Drop tool here>'
  }

  function showQueryOperatorName(query: Query | undefined): string {
    if (query && (query.from.type == 'allArtifacts' || query.from.type == 'traces'))
      return query.from.type
    return 'Drop Query Operator'
  }

  function getSelectValue(select: Select, row: Row): string {
    if (currentQuery.value) {
      const query = removeAllToolBoxAssistFieldsFromSelect(currentQuery.value)
      const origSelect = query.select.find(
        (s) => s.as == select.as && JSON.stringify(s.field) == JSON.stringify(select.field),
      )
      const index = origSelect ? query.select.indexOf(origSelect) : -1
      if (index != -1) {
        const column = previewData.value.columns ? previewData.value.columns[index] : undefined
        if (column && column.id === displayName(select)) {
          const cell = row.cells[index]
          return cell.value
        }
      }
    }
    const columnIndex = previewData.value.columns.findIndex(
      (column: any) => column.id === displayName(select),
    )

    if (columnIndex === -1) {
      return ''
    }
    const cell = row.cells[columnIndex]
    return cell.value
  }

  async function loadCustomMetricsPreview(query: Query) {
    previewData.value = { columns: [], rows: [] }
    let newPreviewData = previewData.value
    if (currentProject.value && currentSnapshot.value) {
      if (hasToolboxAssist(query)) {
        query = removeAllToolBoxAssistFieldsFromSelect(query)
        query = removeExtraToolboxAssistFields(query)
      }
      currentPreviewDataQuery = query
      newPreviewData = await customMetricService
        .getCustomMetricPreview(currentProject.value.id, currentSnapshot.value.id, query)
        .catch(() => {
          const result: CustomMetricPreview = { columns: [], rows: [] }
          return Promise.resolve(result)
        })
      if (currentPreviewDataQuery == query) {
        // if another query was requested after this one, do not update
        previewData.value = newPreviewData
        currentPreviewDataQuery = undefined
      }
    } else {
      console.error(
        `loadCustomMetricsPreview can not get preview data: [project: ${currentProject.value}, currentSnapshot: ${currentSnapshot.value}]`,
      )
    }
  }
</script>

<style scoped>
  tr:first-child th {
    border-top: 1px solid #eaeaea;
  }
  tr:last-child th {
    border-bottom: 1px solid #eaeaea !important;
  }
  .toolbox-table {
    border: 2px solid #eaeaea;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    overflow-y: auto;
    padding: 25px;
  }
  .metric-table-header {
    border-bottom: none !important;
    border-right: 1px solid #eaeaea;
    border-left: 1px solid #eaeaea;
  }

  .metric-table-title {
    display: flex;
    margin: 10px;
    border: 2px solid #eaeaea;
  }

  .metric-table-column-title {
    width: 100%;
    text-align: left;
  }

  .toolbox-table-column {
    border-right: 1px solid #eaeaea;
    border-left: 1px solid #eaeaea;
  }

  .drop-zone,
  .drop-zone-operator,
  .drop-zone-attribute {
    background-color: rgb(245, 245, 245);
    border: 2px solid #eaeaea;
    padding: 10px;
    text-align: center;
    min-height: 50px;
    margin-bottom: 10px;
  }

  .tool-target:hover,
  .active-drop-zone:hover {
    cursor: pointer;
  }

  .drop-zone-new-attribute {
    width: 200px;
  }

  .toolbox-table-attributes .v-col {
    display: flex;
    flex-direction: column;
  }

  .active-drop-zone {
    background-color: rgb(232, 241, 250);
    border: 3px dashed rgb(54, 92, 128);
    padding: 10px;
    text-align: center;
    min-height: 50px;
    color: rgb(54, 92, 128);
  }

  .is-disabled {
    color: #aaa;
    pointer-events: none;
  }

  .is-disabled .drop-zone,
  .is-disabled .drop-zone-attribute {
    border: 2px solid #eaeaea;
  }

  .horizontal-scroll-container {
    flex: 1;
    display: flex;
    overflow-x: auto;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .scroll-item {
    flex: 0 0 auto;
    width: 250px;
    height: 100%;
    display: flex;
    flex-direction: column;
    white-space: nowrap;
  }

  .horizontal-scroll-container .table-column:not(:last-child) {
    border-right: 2px solid #eaeaea;
  }

  .toolbox-table-attributes .v-col {
    display: flex;
    flex-direction: column;
  }

  .full-width {
    width: 100%;
  }
</style>
